import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, Button, Badge } from "@/components/ui";
import { Link2, TrendingUp, ArrowRight, Loader2, Building2 } from "lucide-react";
import { openFinanceApi } from "@/services/api";
import { formatCurrency } from "@/lib/utils";

interface OpenFinanceSummary {
  totalAvailable: string;
  totalBlocked: string;
  accountsCount: number;
  activeLinksCount: number;
  lastSyncAt: string | null;
}

export function OpenFinanceCTA() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<OpenFinanceSummary | null>(null);

  useEffect(() => {
    async function loadSummary() {
      try {
        const data = await openFinanceApi.getSummary();
        setSummary(data);
      } catch (error) {
        console.error("Erro ao carregar resumo Open Finance:", error);
      } finally {
        setLoading(false);
      }
    }
    loadSummary();
  }, []);

  // Se ainda está carregando, mostra skeleton
  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Se não tem contas conectadas, mostra CTA para conectar
  if (!summary || summary.accountsCount === 0) {
    return (
      <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20 hover:border-blue-500/40 transition-colors cursor-pointer" onClick={() => navigate("/open-finance")}>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
              <Link2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-lg">Open Finance</h3>
                <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 text-xs">
                  Novo
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Conecte suas contas bancárias e descubra oportunidades de investir seu dinheiro parado no BRLD.
              </p>
              <Button variant="link" className="p-0 h-auto text-blue-400 hover:text-blue-300">
                Conectar meus bancos
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Se tem contas, mostra resumo de oportunidade
  const totalAvailable = parseFloat(summary.totalAvailable);
  const potentialMonthlyYield = totalAvailable * 0.0095; // ~0.95% ao mês (95% CDI)

  return (
    <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20 hover:border-blue-500/40 transition-colors cursor-pointer" onClick={() => navigate("/open-finance")}>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-lg">Oportunidade Identificada</h3>
              <Badge variant="secondary" className="bg-green-500/20 text-green-400 text-xs">
                Open Finance
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              Você tem <span className="font-semibold text-foreground">{formatCurrency(totalAvailable)}</span> disponível em {summary.accountsCount} conta{summary.accountsCount > 1 ? "s" : ""} bancária{summary.accountsCount > 1 ? "s" : ""}.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Rendimento potencial:</span>{" "}
                <span className="font-medium text-success">+{formatCurrency(potentialMonthlyYield)}/mês</span>
              </div>
            </div>
            <Button variant="link" className="p-0 h-auto text-blue-400 hover:text-blue-300 mt-2">
              Ver detalhes
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Mini lista de contas */}
        {summary.accountsCount > 0 && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-blue-500/20">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {summary.activeLinksCount} banco{summary.activeLinksCount > 1 ? "s" : ""} conectado{summary.activeLinksCount > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
